/**
 * One live match session (operators only, 2026-10-18; API: apps/api/src/routes/live.ts).
 *
 *   Add segment   an upload like any other, made a segment of this session; the first segment's
 *                 calibration becomes the session's, later segments are checked against it
 *   segments      each with the four-stage rail, the pipeline step it is on now (as the operator's
 *                 pipeline page shows it), its calibration, its label review, "Rebuild analysis",
 *                 and its clips
 *   the player    the clip picked, with the render options: speeds (the Reel's gold tiles) and traces
 *                 (the ball, and the hit when the bat met it), drawn over the video
 *   players       the session's batsmen and bowlers, regrouped over every segment as they arrive,
 *                 in the match page's cards; a card opens the player's clips and pitch map
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { live, liveStage, metricNum, zoneCode, type LiveClip, type LiveOverlays, type LiveSegment, type LiveSessionDetail } from "../api/live";
import { useLoad, usePoll } from "../api/hooks";
import type { Player, Roster } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { LiveClipPlayer, RenderOptions, useRenderOptions } from "../components/LiveClipPlayer";
import { liveDeliverySummary } from "../lib/descriptions";
import { PlayerRows } from "../components/PlayerCards";
import { Shell } from "../components/Shell";
import { Bar, Dot, ErrorLine, useToast } from "../components/ui";
import { prefetchClipUrls } from "../lib/clipPrefetch";
import { useIsDesktop } from "../lib/device";
import { fmtDuration, plural } from "../lib/format";
import { counterShare, counterText, whereIs } from "../lib/pipeline";
import { resolveRoster } from "../lib/roster";
import { readDurationSeconds, Uploader, type Progress } from "../lib/upload";
import { OperatorsOnly } from "./AdminPipeline";

const RAIL = ["Uploaded", "Being set up", "Finding every ball", "Clips ready"];
const LENGTH_WORD: Record<string, string> = { yorker: "yorker", full: "full", good: "good length", short: "short" };
const LINE_WORD: Record<string, string> = { off: "outside off", middle: "on the stumps", leg: "leg side" };

/** The segments' overlays, fetched as their clips are opened and again after a rebuild. */
export function useOverlays() {
  const [byUrl, setByUrl] = useState<Record<string, LiveOverlays>>({});
  const asked = useRef<Set<string>>(new Set());
  const want = useCallback((url: string | null, version: string) => {
    if (!url) return;
    const k = `${url}@${version}`;
    if (asked.current.has(k)) return;
    asked.current.add(k);
    void live.overlays(url).then((o) => setByUrl((m) => ({ ...m, [url]: o }))).catch(() => asked.current.delete(k));
  }, []);
  return { byUrl, want };
}

export function clipFacts(c: LiveClip): string[] {
  const out: string[] = [];
  const bowl = metricNum(c.metrics.bowl_speed) ?? metricNum(c.metrics.ball_speed_estimate);
  const bat = metricNum(c.metrics.bat_swing_speed);
  const off = metricNum(c.metrics.ball_impact_speed);
  if (bowl != null) out.push(`ball ${bowl} km/h`);
  if (bat != null) out.push(`bat ${bat} km/h`);
  if (off != null) out.push(`off the bat ${off} km/h`);
  const len = c.metrics.length_class?.status === "ok" ? String(c.metrics.length_class.value) : null;
  const line = c.metrics.line_class?.status === "ok" ? String(c.metrics.line_class.value) : null;
  if (len && line) out.push(`${LENGTH_WORD[len] ?? len}, ${LINE_WORD[line] ?? line} (${zoneCode(c.metrics)})`);
  return out;
}

const mmss = (ms: number | null | undefined) => {
  if (ms == null) return "";
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

function Rail({ stage }: { stage: number }) {
  return (
    <div className="lv-rail" data-testid="segment-rail" data-stage={stage}>
      {RAIL.map((label, i) => (
        <span key={label} className={stage < 0 ? "bad" : i < stage ? "done" : i === stage ? "on" : ""}><i />{label}</span>
      ))}
    </div>
  );
}

function SegmentCard({ seg, selected, onPick, onRebuild, rebuilding }: {
  seg: LiveSegment;
  selected: string | null;
  onPick: (c: LiveClip) => void;
  onRebuild: () => void;
  rebuilding: boolean;
}) {
  const v = seg.video;
  const w = whereIs(v.state, seg.pipeline.job, seg.pipeline.progress, seg.pipeline.attempt?.state ?? null);
  const running = seg.pipeline.job?.state === "running";
  const share = running ? counterShare(seg.pipeline.progress?.progress) : null;
  const cal = seg.calibration;
  const calLine = !cal
    ? v.state === "calibration_queue" || v.state === "calibrating" ? "being calibrated" : "not yet"
    : cal.method === "reuse" && cal.session_setup
      ? `the session's (camera moved ${cal.drift_px != null ? cal.drift_px.toFixed(1) : "?"} px)`
      : cal.method === "auto" && cal.session_setup
        ? "found from the stumps; saved as the session's"
        : `a new one (${cal.method}): the camera moved from the session's`;
  const rv = seg.review;
  const rb = seg.rebuild;
  const rbRunning = rb.job != null && ["created", "dispatched", "running"].includes(rb.job.state);
  const rbLine = rbRunning
    ? "rebuilding…"
    : rb.job?.state === "failed"
      ? `rebuild failed: ${rb.job.error?.code ?? ""}`
      : rb.rebuilt_at
        ? `rebuilt ${new Date(rb.rebuilt_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} · ${plural(rb.summary?.n_changed ?? 0, "clip")} changed${seg.rejected.length ? ` · ${seg.rejected.length} rejected` : ""}`
        : "";
  return (
    <section className="card lv-seg" data-testid="live-segment" data-seq={seg.seq} data-state={v.state} data-review={rv.state ?? ""} data-rebuilt-at={seg.rebuild.rebuilt_at ?? ""}>
      <div className="row between">
        <div className="h3">Segment {seg.seq}</div>
        <span className="tiny">{fmtDuration(v.duration_ms)}{seg.clips.length ? ` · ${plural(seg.clips.length, "clip")}` : ""}</span>
      </div>
      <Rail stage={liveStage(v.state)} />
      <div className="row mt10 lv-where" data-testid="segment-step">
        <Dot kind={w.dot} />
        <span className="med">{w.label}</span>
        {w.detail ? <span className="tiny">{w.detail}</span> : null}
      </div>
      {share != null && (
        <div className="mt6">
          <Bar value={share} />
          <div className="tiny mt6">{counterText(seg.pipeline.progress?.progress)}{seg.pipeline.progress?.part ? ` · ${seg.pipeline.progress.part}` : ""}</div>
        </div>
      )}
      {v.error && <p className="tiny err mt6">{v.error.code}: {v.error.message}</p>}
      <div className="tiny mt10" data-testid="segment-calibration">Calibration: {calLine}</div>
      <div className="tiny mt6" data-testid="segment-review">
        Label review: {rv.state === "staged" ? "in the review queue" : rv.state === "queued" ? "being staged…" : rv.state === "failed" ? `failed (${rv.error ?? ""})` : "after the balls are found"}
        {rv.state === "staged" && rv.url ? <> · <a className="u" href={rv.url} target="_blank" rel="noreferrer" data-testid="open-review">Open in label review ↗</a></> : null}
      </div>
      <div className="row mt10 lv-rebuild">
        <button className="btn sm" disabled={rv.state !== "staged" || rbRunning || rebuilding} onClick={onRebuild} data-testid="rebuild">
          {rbRunning || rebuilding ? "Rebuilding…" : "Rebuild analysis"}
        </button>
        <span className="tiny" data-testid="rebuild-state">{rbLine}</span>
      </div>
      {seg.clips.length > 0 && (
        <div className="lv-clips mt10" data-testid="segment-clips">
          {seg.clips.map((c) => {
            const bowl = metricNum(c.metrics.bowl_speed) ?? metricNum(c.metrics.ball_speed_estimate);
            return (
              <button key={c.id} className={`lv-clip ${selected === c.id ? "on" : ""}`} onClick={() => onPick(c)} onPointerEnter={() => prefetchClipUrls([c.playback_url])} onTouchStart={() => prefetchClipUrls([c.playback_url])} data-testid="clip" data-candidate={c.candidate_id}>
                <span className="med">Clip {c.index}</span>
                <span className="tiny">{mmss(c.events_ms.release ?? c.window_ms?.start)}{c.bowler || c.batsman ? ` · ${c.bowler?.label ?? "?"} to ${c.batsman?.label ?? "?"}` : ""}{bowl != null ? ` · ${bowl} km/h` : ""}</span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

/** The live roster in the match page's card rows: a player's id is its stable key. */
function rosterOf(d: LiveSessionDetail): Roster {
  const players: Player[] = [...d.players.batsmen, ...d.players.bowlers].map((p) => ({ id: p.key, role: p.role, order: p.order, first_seen_ms: 0, crop_url: p.crop_url, clip_count: p.clip_count, name: p.name }));
  return {
    available: true,
    reason: null,
    selectable: true,
    plan_required: null,
    run: null,
    identity: { state: d.players.busy ? "running" : d.players.status === "ok" ? "ready" : d.players.status === "unavailable" ? "unavailable" : "not_run", reason: d.players.reason, job_id: null, updated_at: null, can_request: false, unassigned: null },
    players,
    pairs: [],
    merges: [],
  };
}

export default function LiveSession() {
  const { id = "" } = useParams();
  const { me } = useAuth();
  const nav = useNavigate();
  const desktop = useIsDesktop();
  const sess = useLoad(() => live.session(id), [id]);
  const d = sess.data;
  const moving = !!d && (d.players.busy || d.segments.some((s) => s.video.state !== "ready" && s.video.state !== "failed" && s.video.state !== "unusable" && s.video.state !== "ingest_failed"
    || s.review.state === "queued" || (s.rebuild.job != null && ["created", "dispatched", "running"].includes(s.rebuild.job.state))));
  usePoll(sess.reload, moving ? 4000 : 15000, !!me?.user.is_operator);
  const [render, setRender] = useRenderOptions();
  const [picked, setPicked] = useState<{ video: string; clip: string } | null>(null);
  const { byUrl, want } = useOverlays();
  const [toast, say] = useToast();
  const [rebuilding, setRebuilding] = useState<string | null>(null);
  const [upload, setUpload] = useState<{ name: string; p: Progress | null } | null>(null);
  const [upErr, setUpErr] = useState<Error | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // the first clip of the newest segment with clips, until one is picked
  const allClips = useMemo(() => (d?.segments ?? []).flatMap((s) => s.clips.map((c) => ({ s, c }))), [d]);
  const curIdx = picked ? allClips.findIndex((x) => x.s.video.id === picked.video && x.c.id === picked.clip) : 0;
  const cur = (curIdx >= 0 ? allClips[curIdx] : allClips[0]) ?? null;
  useEffect(() => {
    const k = curIdx >= 0 ? curIdx : 0;
    prefetchClipUrls([
      allClips[k + 1]?.c.playback_url,
      allClips[k + 2]?.c.playback_url,
      allClips[k - 1]?.c.playback_url,
    ]);
  }, [allClips, curIdx]);
  const ovUrl = cur?.s.overlays ?? null;
  useEffect(() => {
    if (cur) want(ovUrl, `${cur.s.rebuild.rebuilt_at ?? ""}`);
  }, [cur, ovUrl, want]);
  if (me && !me.user.is_operator) return <OperatorsOnly shows="live match sessions" />;

  const ovDoc = ovUrl ? byUrl[ovUrl] : undefined;
  const overlay = cur && ovDoc ? ovDoc.deliveries[cur.c.candidate_id] ?? null : null;
  const frame = ovDoc?.frame ?? { width: cur?.s.video.image_size?.[0] ?? 1280, height: cur?.s.video.image_size?.[1] ?? 720, fps: cur?.s.video.fps ?? 29.97 };

  const addSegment = async (file: File) => {
    setUpErr(null);
    setUpload({ name: file.name, p: null });
    try {
      const dur = (await readDurationSeconds(file)) ?? 60;
      const up = new Uploader(file, (p) => setUpload({ name: file.name, p }), { createPath: `/ops/live/sessions/${id}/segments`, storeKey: `dt.live.upload.${id}` });
      await up.run({ pitch_length_choice: d?.session.pitch_length_choice ?? "full", duration_hint_s: Math.max(1, Math.round(dur)) });
      say(`${file.name} uploaded. Its balls are being found.`);
      await sess.reload();
    } catch (e) {
      setUpErr(e as Error);
    } finally {
      setUpload(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const rebuild = async (seg: LiveSegment) => {
    setRebuilding(seg.video.id);
    try {
      await live.rebuild(seg.video.id);
      say(`Segment ${seg.seq}: rebuilding from the label review's labels.`);
      await sess.reload();
    } catch (e) {
      say((e as Error).message);
    } finally {
      setRebuilding(null);
    }
  };
  const session = d?.session;
  const cal = session?.calibration;
  const n = d?.segments.reduce((k, s) => k + s.clips.length, 0) ?? 0;
  const roster = d ? resolveRoster(rosterOf(d)) : null;

  const playerPanel = cur ? (
    <div className="lv-stage" data-testid="live-stage">
      <LiveClipPlayer src={cur.c.playback_url} overlay={overlay} frameSize={frame} fps={frame.fps} showSpeeds={render.speeds} showTraces={render.traces} autoPlay label={`Segment ${cur.s.seq} clip ${cur.c.index}`} description={liveDeliverySummary(cur.c)} />
      <RenderOptions speeds={render.speeds} traces={render.traces} onChange={setRender} />
      <div className="mt6">
        <div className="med">Segment {cur.s.seq} · clip {cur.c.index}{cur.c.bowler || cur.c.batsman ? ` · ${cur.c.bowler?.label ?? "?"} to ${cur.c.batsman?.label ?? "?"}` : ""}</div>
        <div className="tiny" data-testid="clip-facts">{clipFacts(cur.c).join(" · ") || "No measurements for this clip."}</div>
        {overlay && <div className="tiny">Traces: {overlay.events_from === "marks" ? "the reviewer's marks" : "the pipeline's events"} · {overlay.counts.click ?? 0} reviewed points, {overlay.counts.track ?? 0} tracked, {overlay.counts.filled ?? 0} filled{overlay.contact ? "" : " · no bat contact: no hit drawn"}</div>}
        {!overlay && cur.s.review.state !== "staged" && <div className="tiny">The overlays come with the segment's label review staging.</div>}
      </div>
    </div>
  ) : (
    <div className="lv-stage lv-empty card"><p className="sub">The clips show here once a segment's balls are found.</p></div>
  );

  return (
    <Shell title={session?.title ?? "Live"} back="/admin/live">
      <div className="p">
        <div className="row between mt16 wrap-r">
          <div>
            <div className="tiny">LIVE MATCH</div>
            <h1 data-testid="live-session-title">{session?.title ?? "…"}</h1>
            <div className="sub" data-testid="live-session-cal">
              {d ? `${plural(d.segments.length, "segment")} · ${plural(n, "clip")} · ` : ""}
              {cal ? `calibration from segment ${cal.segment_seq ?? "?"}, used by every later segment` : "calibrated by the first segment"}
            </div>
          </div>
          <div className="lv-add">
            <input ref={fileRef} type="file" accept="video/*" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void addSegment(f); }} data-testid="segment-file" />
            <button className="btn dark" disabled={!!upload || session?.state === "closed"} onClick={() => fileRef.current?.click()} data-testid="add-segment">{upload ? "Uploading…" : "Add segment"}</button>
          </div>
        </div>
        {upload && (
          <div className="card mt16" data-testid="segment-uploading">
            <div className="row between"><span className="med">{upload.name}</span><span className="tiny" data-testid="segment-upload-pct">{upload.p ? Math.floor((100 * upload.p.sent) / Math.max(1, upload.p.total)) : 0}%</span></div>
            <Bar value={upload.p ? upload.p.sent / Math.max(1, upload.p.total) : 0} />
          </div>
        )}
        <ErrorLine error={upErr} />
        <ErrorLine error={sess.error} />
        <div className={desktop ? "lv-grid mt24" : "mt16"}>
          {!desktop && playerPanel}
          <div className="stack" data-testid="live-segments">
            {d?.segments.map((s) => (
              <SegmentCard key={s.video.id} seg={s} selected={cur?.s.video.id === s.video.id ? cur.c.id : null}
                onPick={(c) => setPicked({ video: s.video.id, clip: c.id })} onRebuild={() => void rebuild(s)} rebuilding={rebuilding === s.video.id} />
            ))}
            {d && d.segments.length === 0 && <div className="card"><p className="sub">No segments yet. Add the first segment: its calibration is saved for the match.</p></div>}
          </div>
          {desktop && <div className="lv-side">{playerPanel}</div>}
        </div>
        <section className="mt32" data-testid="live-players">
          <div className="row between">
            <h2>Players</h2>
            <span className="tiny">{d?.players.busy ? "regrouping over the segments…" : d?.players.segments.length ? `grouped over ${plural(d.players.segments.length, "segment")}` : ""}</span>
          </div>
          {roster && (roster.batsmen.length || roster.bowlers.length) ? (
            <PlayerRows roster={roster} merging={false}
              onOpen={(p) => nav(`/admin/live/${id}/players/${p.id}`)}
              onRename={async (p, name) => {
                await live.rename(id, p.id, name);
                await sess.reload();
              }}
              onMerge={async () => false}
              onUndo={async () => false} />
          ) : (
            <p className="tiny mt10">{d?.players.status === "unavailable" ? `The players could not be grouped yet (${d.players.reason ?? ""}).` : "The players show here once a segment is in label review."}</p>
          )}
        </section>
      </div>
      {toast}
    </Shell>
  );
}
